const article = {
  slug: "what-is-a-cma",
  cluster: "seller",
  title: "What Is a CMA? How to Compare Your Home to Recent Sales",
  description:
    "What is a CMA in real estate? A plain-English guide to the comparative market analysis: how it works with recent sold comps, the rule of three, basic adjustments, DIY and FSBO pricing.",
  audience: "Sellers & agents",
  funnelStage: "MOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "7 min read",
  tldr: "A CMA (comparative market analysis) estimates a home's value by comparing it to similar homes that sold nearby in the last 3–6 months. Anchor on at least three sold comps (the \\\"rule of three\\\"), then adjust each comp's price up or down for differences versus your home — subtract for better comps, add for worse ones. The result is a price range, not a single number. It's a pricing tool, not a formal appraisal.",
  blocks: [
    {
      type: "p",
      spans: [
        "A CMA (comparative market analysis) is an estimate of what a home is worth right now, built by comparing it to similar nearby properties that recently **sold**. It's the core tool agents use to price a listing and advise sellers — and it's how buyers decide whether a list price is fair. A good CMA isn't a guess or an online estimate; it's a side-by-side comparison of your home against real, recent, comparable sales, adjusted for the differences between them.",
      ],
    },
    {
      type: "callout",
      spans: [
        "In one sentence: a CMA prices your home by asking what buyers actually paid for the most similar homes nearby in the last few months — then adjusting for the ways your home is better or worse.",
      ],
    },
    {
      type: "h2",
      text: "What does CMA stand for, and what is it?",
      id: "what-does-cma-stand-for",
    },
    {
      type: "p",
      spans: [
        "CMA stands for **comparative market analysis**. It's a report — usually prepared by a real estate agent — that estimates a home's likely sale price by comparing it to \"comps\": comparable homes in the same area that have sold recently, that are currently for sale, or that failed to sell. The sold comps carry the most weight, because they show what buyers were actually willing to pay, not what a seller hoped to get.",
      ],
    },
    {
      type: "p",
      spans: [
        "A CMA is not an appraisal. An appraisal is a formal valuation by a licensed appraiser, usually ordered by a lender to protect a loan. A CMA is a pricing tool used for strategy — to set a list price, evaluate an offer, or win a ",
        {
          text: "listing appointment",
          href: "/resources/how-to-win-the-listing-appointment",
        },
        ". It also isn't a Zestimate or other automated estimate: those use broad algorithms and can't see your kitchen remodel, your busy-road location, or last month's sale two doors down.",
      ],
    },
    {
      type: "table",
      headers: ["Tool", "Who makes it", "Best for"],
      rows: [
        ["CMA", "Real estate agent (or you)", "Setting a list price; evaluating an offer"],
        ["Appraisal", "Licensed appraiser", "Lender loan approval; formal value"],
        ["Automated estimate (e.g. Zestimate)", "An algorithm", "A rough starting ballpark only"],
      ],
    },
    {
      type: "h2",
      text: "How does a CMA work?",
      id: "how-a-cma-works",
    },
    {
      type: "p",
      spans: [
        "Every solid CMA follows the same logic: find homes as similar to yours as possible that recently sold, then adjust their sale prices up or down to account for the differences. The closer the comps and the smaller the adjustments, the more reliable the result.",
      ],
    },
    {
      type: "h3",
      text: "Use recent SOLD comps (within 3–6 months)",
      id: "recent-sold-comps",
    },
    {
      type: "p",
      spans: [
        "The comps that matter most are homes that actually **closed** — sold and recorded — within roughly the last 3 to 6 months. Recency matters because the market moves; a sale from last year may reflect a different market than the one you're listing into. Active listings tell you about your competition, and expired listings tell you what was priced too high, but only sold comps tell you what buyers truly paid. If your area is moving fast, lean toward the most recent sales; if sales are sparse, you may need to stretch the window a little and weight the freshest ones.",
      ],
    },
    {
      type: "h3",
      text: "Apply the rule of three",
      id: "rule-of-three",
    },
    {
      type: "p",
      spans: [
        'The "rule of three" is the practitioner\'s shorthand: anchor your estimate on at least **three** strong sold comps. One comp can be a fluke — an unusual buyer, a hidden defect, a family sale. Three or more similar sales clustered in a price range give you a defensible value and reveal a pattern. If you can, pull three recent solds, plus a couple of active listings (your competition) and an expired or two (the ceiling buyers rejected).',
      ],
    },
    {
      type: "h3",
      text: "Make basic adjustments",
      id: "basic-adjustments",
    },
    {
      type: "p",
      spans: [
        "No two homes are identical, so you adjust each comp's sale price to \"convert\" it into your home. The principle: adjust the **comp**, not your home. If a comp is better than yours, subtract value from its price; if it's worse, add value. The goal is to estimate what that comp would have sold for if it were as similar to your home as possible.",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "**Better comp → subtract.** If a comp has an extra bedroom, a finished basement, or a renovated kitchen your home lacks, lower its price to match yours.",
        ],
        [
          "**Worse comp → add.** If a comp has a smaller lot, an older roof, or no garage, raise its price to match yours.",
        ],
        [
          "**Size, condition, location, and features** are the big four. A quieter street, an extra bath, or a recent remodel can each move value meaningfully.",
        ],
        [
          "**Keep adjustments honest.** Many small adjustments signal the comp isn't really comparable — find a closer one instead.",
        ],
      ],
    },
    {
      type: "stat",
      value: "The best comp is one that needs almost no adjusting",
      label: "Closer comps beat clever math every time",
    },
    {
      type: "h2",
      text: "What makes a home a good comp?",
      id: "what-makes-a-good-comp",
    },
    {
      type: "p",
      spans: [
        "A strong comp is similar to your home on the dimensions buyers actually shop by. Aim to match as many of these as you can, then adjust for the rest:",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "**Location** — same neighborhood, same school zone, ideally within about a mile and on the same side of any major dividing road or boundary.",
        ],
        ["**Size** — similar square footage, bedroom and bathroom count, and lot size."],
        [
          "**Type and age** — same property type (single-family, townhome, condo) and a comparable era of construction.",
        ],
        ["**Condition and updates** — similar level of renovation, finishes, and upkeep."],
        ["**Timing** — sold recently, ideally within 3–6 months."],
      ],
    },
    {
      type: "p",
      spans: [
        "Fair-housing note: comps are about the **property and its use**, never about who lives there. Compare homes, schools as a factual zone, commute, and features — never the makeup of a neighborhood's residents.",
      ],
    },
    {
      type: "h2",
      text: "How do I do a rough CMA myself?",
      id: "diy-cma",
    },
    {
      type: "p",
      spans: [
        "You won't have full MLS access as a seller, but you can build a credible rough CMA from public listing sites. Here's the process agents follow, simplified:",
      ],
    },
    {
      type: "ol",
      items: [
        [
          '**Pull recent sold listings** near you — filter for "sold" or "recently sold," then narrow to the last 3–6 months.',
        ],
        [
          "**Filter to homes like yours** — similar square footage (aim within ~15%), same bed/bath count, same property type, and as close geographically as you can get.",
        ],
        ["**Pick your three (or more) best comps** — the ones that need the least explaining."],
        [
          "**Adjust each comp's sale price** up or down for the differences vs. your home (subtract for better, add for worse).",
        ],
        [
          "**Take the range, not a single number** — your home's value is the cluster the adjusted comps point to. Where you price inside that range depends on your urgency and the home's condition.",
        ],
        [
          "**Sanity-check against active listings** — those are the homes buyers will compare yours to right now.",
        ],
      ],
    },
    {
      type: "callout",
      spans: [
        "Price to the range, then position deliberately. Want speed? Price at or just under the cluster. Have a standout home and time to wait? The top of the range can work — but a price clearly above your sold comps is the number-one reason homes sit. See ",
        {
          text: "is my house overpriced?",
          href: "/resources/is-my-house-overpriced",
        },
      ],
    },
    {
      type: "h2",
      text: "The FSBO angle: can a CMA replace an agent's pricing?",
      id: "fsbo-cma",
    },
    {
      type: "p",
      spans: [
        "If you're selling for sale by owner, a do-it-yourself CMA is one of the most valuable hours you can spend — pricing is the lever that most determines whether you sell quickly and for full value. A careful DIY CMA gets you a defensible range. Its limits: you can't see private sale notes, off-market deals, or the concessions buried in a closed deal, and it's genuinely hard to judge your own home's condition objectively against others. Treat your number as a strong starting range, then pressure-test it against the active listings buyers will line yours up against. For a deeper walkthrough, see ",
        {
          text: "how to price a listing with a CMA",
          href: "/resources/how-to-price-a-listing-cma",
        },
        ".",
      ],
    },
    {
      type: "h2",
      text: "How Listino automates your CMA",
      id: "how-listino-automates-cma",
    },
    {
      type: "p",
      spans: [
        "Building a CMA by hand is doable but tedious — pulling comps, filtering them, and reasoning through each adjustment takes time and a trained eye. A ",
        {
          text: "Listino listing prep",
          href: "/get-started",
        },
        " does the comparative market analysis for you: its Quick CMA pulls recent sold comps near your property, weighs them against your home, and shows your price position so you can see, at a glance, whether your number sits below, inside, or above what the market is paying. It pairs the Quick CMA with the rest of the prep — an optimized description, photo sequencing, and projected outcomes — so pricing isn't the only thing you get right. ",
        "Every account starts free with a monthly allowance of credits, and credits pay for each listing you prepare; see ",
        { text: "pricing", href: "/pricing" },
        " for what things cost.",
      ],
    },
    {
      type: "p",
      spans: [
        "Whether you build it by hand or let Listino do it, the discipline is the same: lean on recent sold comps, anchor on at least three, adjust the comp toward your home, and price to the range the evidence supports. Get that right and most of the hard part of selling is already handled.",
      ],
    },
  ],
  faqs: [
    {
      question: "What is the difference between a CMA and an appraisal?",
      answer: [
        {
          type: "p",
          spans: [
            "A CMA (comparative market analysis) is a pricing tool, usually prepared by a real estate agent, that estimates a home's likely sale price by comparing it to recent comparable sales. An appraisal is a formal valuation performed by a licensed appraiser, typically ordered by a lender to confirm a property is worth the loan amount. A CMA guides strategy — setting a list price or evaluating an offer — while an appraisal protects a mortgage. They often land in the same range, but they serve different purposes and carry different legal weight.",
          ],
        },
      ],
    },
    {
      question: "How many comps do you need for a CMA?",
      answer: [
        {
          type: "p",
          spans: [
            'Aim for at least three strong sold comps — the "rule of three." One sale can be a fluke caused by an unusual buyer or a hidden issue, but three or more similar recent sales reveal a pattern and give you a defensible price range. Many agents also include a couple of active listings to show current competition and an expired listing or two to mark the price ceiling buyers already rejected.',
          ],
        },
      ],
    },
    {
      question: "How recent should comps be in a CMA?",
      answer: [
        {
          type: "p",
          spans: [
            "Use homes that sold within roughly the last 3 to 6 months. The market shifts over time, so recent closed sales best reflect what buyers are paying today. In fast-moving markets, weight the freshest sales most heavily. If your area has few recent sales, you may need to widen the window, but always give the most recent comps the most weight.",
          ],
        },
      ],
    },
    {
      question: "Can I do my own CMA as a FSBO seller?",
      answer: [
        {
          type: "p",
          spans: [
            "Yes. You can build a credible rough CMA from public listing sites by pulling recently sold homes near you, filtering to properties similar to yours, choosing your three best comps, and adjusting each one's sale price for differences versus your home. You won't see private MLS notes or off-market deals, and it's hard to judge your own home objectively, so treat your number as a strong starting range and pressure-test it against active listings — or have a CMA run for you to confirm it.",
          ],
        },
      ],
    },
    {
      question: "Is a Zestimate the same as a CMA?",
      answer: [
        {
          type: "p",
          spans: [
            "No. A Zestimate (and similar automated estimates) is generated by an algorithm using broad data, and it can't see your renovations, your specific location quirks, or the most recent nearby sale. A CMA is built from hand-selected comparable sales and human-judged adjustments for condition, features, and location. An automated estimate is fine as a rough starting ballpark, but a CMA is what you price on.",
          ],
        },
      ],
    },
  ],
  related: [
    "how-to-win-the-listing-appointment",
    "is-my-house-overpriced",
    "how-to-price-a-listing-cma",
  ],
};

export default article;
